<template>
  <div
    v-if="activeTour"
    class="tour-system"
  >
    <!-- Overlay for highlighting elements -->
    <div
      v-if="currentStep"
      class="tour-overlay"
      @click.self="handleOverlayClick"
    >
      <div
        v-if="highlightElement"
        class="tour-highlight"
        :style="highlightStyle"
      />
    </div>

    <!-- Tour Tooltip/Info Box -->
    <v-card
      v-if="currentStep"
      class="tour-card elevation-3"
      :style="cardPosition"
    >
      <v-card-title class="tour-card-title pa-3 pb-2 subtitle-2">
        <div class="tour-card-title__content">
          <div class="tour-eyebrow caption">
            Guided tour
          </div>
          <div class="tour-title-text">
            {{ currentStep.title }}
          </div>
        </div>
        <v-spacer />
        <v-btn
          icon
          x-small
          color="actiongray"
          class="tour-close-btn"
          aria-label="Close tour"
          @click="skipTour"
        >
          <font-awesome-icon
            :icon="['fas', 'times']"
            size="sm"
          />
        </v-btn>
      </v-card-title>

      <v-card-text class="tour-card-body pa-3 pt-2 pb-3">
        {{ currentStep.content }}
      </v-card-text>

      <!-- Progress Bar -->
      <v-progress-linear
        :value="progressPercent"
        height="3"
        color="blue"
        background-color="offwhite"
        class="tour-progress ma-0"
      />

      <v-card-actions class="pa-3 pt-2">
        <div class="tour-progress-label caption">
          {{ currentIndex + 1 }} of {{ activeTour.steps.length }}
        </div>
        <v-spacer />

        <v-btn
          outlined
          class="text-none tour-nav-btn"
          :disabled="currentIndex === 0"
          @click="previousStep"
        >
          <font-awesome-icon
            :icon="['fas', 'chevron-left']"
            size="xs"
            class="mr-1"
          />
          Back
        </v-btn>

        <v-btn
          v-if="isLastStep"
          outlined
          color="blue"
          class="text-none ml-2 tour-nav-btn"
          @click="completeTour"
        >
          Complete
          <font-awesome-icon
            :icon="['fas', 'check']"
            size="xs"
            class="ml-1"
          />
        </v-btn>

        <v-btn
          v-else
          outlined
          color="blue"
          class="text-none ml-2 tour-nav-btn"
          @click="nextStep"
        >
          Next
          <font-awesome-icon
            :icon="['fas', 'chevron-right']"
            size="xs"
            class="ml-1"
          />
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Keyboard Navigation Hint -->
    <div
      class="tour-hint caption pa-2"
    >
      <span class="grey--text">
        ESC to close • ← → arrows to navigate
      </span>
    </div>
  </div>
</template>

<script>
import tourManager from '@/dashboard/utils/tourManager'
import { tourDefinitions } from '@/dashboard/utils/tourDefinitions'

export default {
  name: 'TourSystem',

  data() {
    return {
      activeTour: null,
      currentIndex: 0,
      highlightElement: null,
      highlightStyle: {},
      cardPosition: {},
    }
  },

  computed: {
    currentStep() {
      if (!this.activeTour || !this.activeTour.steps) return null
      return this.activeTour.steps[this.currentIndex]
    },

    isLastStep() {
      if (!this.activeTour) return false
      return this.currentIndex === this.activeTour.steps.length - 1
    },

    progressPercent() {
      if (!this.activeTour) return 0
      return ((this.currentIndex + 1) / this.activeTour.steps.length) * 100
    },
  },

  watch: {
    currentStep: {
      immediate: true,
      handler() {
        this.$nextTick(() => {
          this.updateHighlight()
        })
      },
    },
  },

  mounted() {
    this.initKeyboardNavigation()
    // Listen for tour start requests from other components
    this.$root.$on('tour:start', this.handleTourStart)
    this.$root.$on('tour:resume', this.handleTourResume)
  },

  beforeDestroy() {
    this.$root.$off('tour:start', this.handleTourStart)
    this.$root.$off('tour:resume', this.handleTourResume)
    document.removeEventListener('keydown', this.handleKeydown)
  },

  methods: {
    /**
     * Start a tour by ID
     */
    handleTourStart(tourId) {
      const tour = tourManager.startTour(tourId, tourDefinitions)
      if (tour) {
        this.activeTour = tour
        this.currentIndex = 0
      }
    },

    /**
     * Resume a tour from saved progress
     */
    handleTourResume(tourId) {
      const resumedTour = tourManager.resumeTour(tourId, tourDefinitions)
      if (resumedTour?.tour) {
        this.activeTour = resumedTour.tour
        this.currentIndex = resumedTour.step || 0
        return
      }

      this.handleTourStart(tourId)
    },

    /**
     * Update highlight position and card position
     */
    updateHighlight() {
      if (!this.currentStep) {
        this.highlightElement = null
        return
      }

      const element = this.currentStep.element
        ? document.querySelector(this.currentStep.element)
        : null

      this.highlightElement = element

      if (element) {
        const rect = element.getBoundingClientRect()
        this.highlightStyle = {
          position: 'fixed',
          top: `${rect.top - 4}px`,
          left: `${rect.left - 4}px`,
          width: `${rect.width + 8}px`,
          height: `${rect.height + 8}px`,
          zIndex: 999,
          pointerEvents: 'none',
          border: '2px solid #007DD6',
          borderRadius: '6px',
          boxShadow: '0 0 0 2px rgba(0, 125, 214, 0.22), 0 8px 20px rgba(0, 12, 21, 0.2)',
        }

        this.positionCard(rect)
      } else {
        this.highlightElement = null
        this.cardPosition = this.getCenterCardPosition()
      }
    },

    /**
     * Position card near highlighted element
     */
    positionCard(elementRect) {
      const gap = 16
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      const cardWidth = Math.min(380, viewportWidth - (gap * 2))
      const cardHeight = 240

      let top
      let left

      // Prefer bottom, fallback to top
      if (elementRect.bottom + cardHeight + gap < viewportHeight) {
        top = elementRect.bottom + gap
      } else {
        top = Math.max(gap, elementRect.top - cardHeight - gap)
      }

      // Center horizontally, adjust if off-screen
      left = elementRect.left + elementRect.width / 2 - cardWidth / 2
      if (left + cardWidth > viewportWidth) {
        left = viewportWidth - cardWidth - gap
      }
      if (left < 0) {
        left = gap
      }

      this.cardPosition = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${cardWidth}px`,
        zIndex: 1000,
      }
    },

    /**
     * Get centered card position (for intro/outro steps)
     */
    getCenterCardPosition() {
      const gap = 16
      const cardWidth = Math.min(380, window.innerWidth - (gap * 2))
      return {
        position: 'fixed',
        top: '50%',
        left: '50%',
        width: `${cardWidth}px`,
        transform: 'translate(-50%, -50%)',
        zIndex: 1000,
      }
    },

    /**
     * Navigate to next step
     */
    nextStep() {
      if (!this.activeTour) return

      const next = tourManager.nextStep()
      if (next) {
        const progress = tourManager.getProgress()
        this.currentIndex = progress?.currentStep ?? (this.currentIndex + 1)
      }
    },

    /**
     * Navigate to previous step
     */
    previousStep() {
      if (!this.activeTour) return

      const previous = tourManager.previousStep()
      if (previous) {
        const progress = tourManager.getProgress()
        this.currentIndex = progress?.currentStep ?? (this.currentIndex - 1)
      }
    },

    /**
     * Complete tour
     */
    completeTour() {
      const completedStepId = this.currentStep?.id || null
      tourManager.completeTour()
      this.activeTour = null
      this.currentIndex = 0
      this.$root.$emit('tour:completed', completedStepId)
    },

    /**
     * Skip tour without completing
     */
    skipTour() {
      tourManager.skipTour()
      this.activeTour = null
      this.currentIndex = 0
      this.$root.$emit('tour:skipped')
    },

    /**
     * Handle overlay click (close tour)
     */
    handleOverlayClick() {
      this.skipTour()
    },

    /**
     * Set up keyboard navigation
     */
    initKeyboardNavigation() {
      this.handleKeydown = (e) => {
        if (!this.activeTour) return

        switch (e.key) {
          case 'Escape':
            this.skipTour()
            break
          case 'ArrowLeft':
            this.previousStep()
            e.preventDefault()
            break
          case 'ArrowRight':
            this.nextStep()
            e.preventDefault()
            break
          default:
            break
        }
      }

      document.addEventListener('keydown', this.handleKeydown)
    },
  },
}
</script>

<style scoped lang="scss">
@import "~src/styles/variables";

.tour-system {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 998;

  .tour-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.6);
    pointer-events: auto;
    z-index: 999;

    .tour-highlight {
      position: fixed;
      transition: all 0.3s ease;
    }
  }

  .tour-card {
    pointer-events: auto;
    max-width: 420px;
    border-radius: 10px;
    border: 1px solid rgba(100, 123, 141, 0.24);
    box-shadow: 0 16px 40px rgba(0, 12, 21, 0.26) !important;
    background: linear-gradient(180deg, #ffffff 0%, #f8fbfe 100%);
  }

  .tour-card-title {
    align-items: flex-start;
  }

  .tour-card-title__content {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  .tour-eyebrow {
    color: $color-monochrome-gray;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    line-height: 1.2;
  }

  .tour-title-text {
    color: $color-monochrome-nearblack;
    font-weight: 600;
    line-height: 1.35;
    margin-top: 2px;
  }

  .tour-card-body {
    color: $color-monochrome-coolgray;
    line-height: 1.5;
  }

  .tour-progress {
    border-top: 1px solid rgba(100, 123, 141, 0.12);
  }

  .tour-progress-label {
    color: $color-monochrome-actiongray;
    font-weight: 600;
  }

  .tour-nav-btn {
    min-width: 84px;
    border-radius: 6px;
  }

  .tour-close-btn {
    margin-top: 2px;
  }

  .tour-hint {
    position: fixed;
    right: 20px;
    bottom: 20px;
    z-index: 999;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(100, 123, 141, 0.18);
    backdrop-filter: blur(2px);
  }

  @media (max-width: 959px) {
    .tour-hint {
      left: 16px;
      right: 16px;
      bottom: 16px;
      text-align: center;
    }

    .tour-nav-btn {
      min-width: 76px;
    }
  }
}
</style>
